import {CatalogConfig, CatalogItem} from '../types'
import {StatusFilter} from './catalogFilter'

// Pure helpers for the OPT-IN server-side table mode (config.serverSide). Where the client-side
// path (catalogFilter) holds the whole catalog and filters in memory, these build the query
// params for a single server page, map the DRF LimitOffset response back into pagination state,
// and turn a fetched dataset into CSV. Kept dependency-free (no React, no apiClient, no intl) so
// they are trivially unit-testable — the hook and page just wire them up.

export type SortDir = 'asc' | 'desc'

export interface Sort {
  // CatalogFieldConfig.name of the sorted column.
  field: string
  dir: SortDir
}

export interface ServerQuery {
  search: string
  status: StatusFilter
  sort: Sort | null
  // 1-based page number.
  page: number
  pageSize: number
}

const DEFAULT_SEARCH_PARAM = 'search'
const DEFAULT_ORDERING_PARAM = 'ordering'

// DRF ordering token for a Sort: 'field' for ascending, '-field' for descending.
export const orderingToken = (sort: Sort): string =>
  sort.dir === 'desc' ? `-${sort.field}` : sort.field

// Whether a column is server-sortable per config.serverSide.sortableFields.
export const isSortable = (config: CatalogConfig, field: string): boolean =>
  Boolean(config.serverSide?.sortableFields?.includes(field))

// Header click-cycle for one column: unsorted → asc → desc → unsorted. Returning null (unsorted)
// lets the backend fall back to config.serverSide.defaultOrdering.
export const nextSort = (current: Sort | null, field: string): Sort | null => {
  if (!current || current.field !== field) return {field, dir: 'asc'}
  if (current.dir === 'asc') return {field, dir: 'desc'}
  return null
}

// Build the query params for one server-side page request. Only emits a param the endpoint has
// opted into (search/ordering names default to DRF's conventions; status is emitted ONLY when the
// config declares a statusParam AND has a soft-delete convention to map through), so an endpoint
// never receives a filter it can't honour.
export const buildServerParams = (
  config: CatalogConfig,
  {search, status, sort, page, pageSize}: ServerQuery
): Record<string, string | number | boolean> => {
  const ss = config.serverSide ?? {}
  const params: Record<string, string | number | boolean> = {
    limit: pageSize,
    offset: Math.max(0, (Math.max(1, page) - 1) * pageSize),
  }

  const trimmed = search.trim()
  if (trimmed) params[ss.searchParam ?? DEFAULT_SEARCH_PARAM] = trimmed

  // Explicit sort wins; otherwise the configured default ordering (if any) is applied.
  const ordering = sort ? orderingToken(sort) : ss.defaultOrdering
  if (ordering) params[ss.orderingParam ?? DEFAULT_ORDERING_PARAM] = ordering

  // Map the active/inactive dropdown to the model's own soft-delete value (so an `is_deleted`
  // model — activeValue false — sends the correct boolean), only when both the server param and
  // the soft-delete convention exist.
  if (ss.statusParam && config.softDelete && status !== 'all') {
    const activeValue = config.softDelete.activeValue
    params[ss.statusParam] = status === 'active' ? activeValue : !activeValue
  }

  return params
}

export interface ServerResult {
  items: CatalogItem[]
  total: number
  totalPages: number
}

// Map a DRF LimitOffset response (`{results, count}`) into the pagination state the screen renders.
// A missing count falls back to the page length so the table still shows what it received.
export const mapServerResult = (
  data: {results?: CatalogItem[]; count?: number} | undefined,
  pageSize: number
): ServerResult => {
  const items = data?.results ?? []
  const total = typeof data?.count === 'number' ? data.count : items.length
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)))
  return {items, total, totalPages}
}

// --- CSV export ---

// RFC-4180 cell escaping: wrap in quotes and double any embedded quote when the value contains a
// comma, quote or newline.
export const csvCell = (value: unknown): string => {
  const s = value === null || value === undefined ? '' : String(value)
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

// Build a CSV document (CRLF line endings, per RFC 4180) from a header row + data rows.
export const buildCsv = (headers: string[], rows: unknown[][]): string =>
  [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n')
